import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { BarChart3, Activity, AlertTriangle, Users, Coins } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const RANGES = [
  { d: 7, label: "7d" }, { d: 14, label: "14d" }, { d: 30, label: "30d" }, { d: 60, label: "60d" }, { d: 90, label: "90d" },
];

const MODULE_COLORS = {
  chat: "#00E5FF", image: "#8A52FF", video: "#F472B6", "video-editor": "#34D399",
  website: "#F59E0B", files: "#60A5FA", auth: "#94A3B8", billing: "#FBBF24",
  projects: "#A78BFA", admin: "#EF4444",
};

export default function AdminAnalytics() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/admin/analytics/overview?days=${days}`).then((r) => setData(r.data)).catch(() => {});
  }, [days]);

  useEffect(() => {
    if (!selectedModule) { setDetail(null); return; }
    api.get(`/admin/analytics/module/${selectedModule}?days=${days}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [selectedModule, days]);

  const totals = data?.totals || {};
  const modules = data?.modules || [];
  const daily = data?.daily || [];

  const topModules = useMemo(() => modules.slice(0, 8).map((m) => ({ name: m.module, events: m.events, users: m.active_users, credits: m.credits_consumed })), [modules]);

  if (user?.role !== "super_admin" && user?.role !== "admin") {
    return <div className="p-8 text-white/50">Admin access required.</div>;
  }

  return (
    <div className="p-8 max-w-[1600px]">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kr-overline text-[#8A52FF]">[ SUPER ADMIN · ANALYTICS ]</div>
          <h1 className="font-display text-4xl mt-2 tracking-tight" data-testid="admin-analytics-title">Platform Analytics</h1>
          <p className="text-white/55 text-sm mt-1.5 max-w-2xl">Active users, credits consumed, errors and usage patterns across every module.</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[110px] h-9 bg-[#050505] border-white/10 text-sm" data-testid="admin-analytics-range"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0E] border-white/10 text-white">{RANGES.map(r => <SelectItem key={r.d} value={String(r.d)}>Last {r.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* KPI cards */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Active users" value={totals.active_users} icon={Users} accent="#00E5FF" />
        <KPI label="Events" value={totals.events} icon={Activity} accent="#8A52FF" />
        <KPI label="Credits consumed" value={totals.credits} icon={Coins} accent="#FBBF24" />
        <KPI label="Errors" value={totals.errors} sub={totals.events ? `${(totals.errors / totals.events * 100).toFixed(1)}% failure rate` : ""} icon={AlertTriangle} accent="#EF4444" />
      </div>

      {/* Daily activity chart */}
      <div className="mt-6 kr-card p-4">
        <div className="flex items-center justify-between">
          <div className="kr-overline">DAILY ACTIVITY</div>
          <div className="text-[10px] text-white/40 font-mono">events · credits · errors · active users</div>
        </div>
        <div className="mt-3 h-64" data-testid="admin-analytics-daily">
          <ResponsiveContainer>
            <LineChart data={daily}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" style={{ fontSize: 10 }} />
              <YAxis stroke="rgba(255,255,255,0.4)" style={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ background: "#050505", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
              <Line type="monotone" dataKey="events" stroke="#00E5FF" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="active_users" stroke="#8A52FF" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="errors" stroke="#EF4444" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Per module bars */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="kr-card p-4">
          <div className="kr-overline">EVENTS PER MODULE</div>
          <div className="mt-3 h-64" data-testid="admin-analytics-module-events">
            <ResponsiveContainer>
              <BarChart data={topModules}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.4)" style={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis stroke="rgba(255,255,255,0.4)" style={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#050505", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
                <Bar dataKey="events" fill="#00E5FF" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="kr-card p-4">
          <div className="kr-overline">CREDITS CONSUMED PER MODULE</div>
          <div className="mt-3 h-64" data-testid="admin-analytics-module-credits">
            <ResponsiveContainer>
              <BarChart data={topModules}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="name" stroke="rgba(255,255,255,0.4)" style={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis stroke="rgba(255,255,255,0.4)" style={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#050505", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
                <Bar dataKey="credits" fill="#FBBF24" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Module table */}
      <div className="mt-6 border border-white/5">
        <table className="w-full text-xs">
          <thead className="bg-[#050505] text-white/50">
            <tr>
              <th className="text-left px-3 py-2.5 font-normal">Module</th>
              <th className="text-right px-3 py-2.5 font-normal">Events</th>
              <th className="text-right px-3 py-2.5 font-normal">Active users</th>
              <th className="text-right px-3 py-2.5 font-normal">Credits</th>
              <th className="text-right px-3 py-2.5 font-normal">Errors</th>
              <th className="text-right px-3 py-2.5 font-normal">Error rate</th>
              <th className="text-right px-3 py-2.5 font-normal">Historical</th>
              <th className="text-right px-3 py-2.5 font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => (
              <tr key={m.module} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-analytics-row-${m.module}`}>
                <td className="px-3 py-2.5"><span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: MODULE_COLORS[m.module] || "#fff" }} />{m.module}</td>
                <td className="px-3 py-2.5 text-right font-mono">{m.events.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-right font-mono">{m.active_users.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-right font-mono">{m.credits_consumed.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-right font-mono">{m.errors}</td>
                <td className="px-3 py-2.5 text-right font-mono">{m.error_rate.toFixed(1)}%</td>
                <td className="px-3 py-2.5 text-right text-white/40 font-mono">{m.imported}</td>
                <td className="px-3 py-2.5 text-right"><button onClick={() => setSelectedModule(m.module)} className="text-[#00E5FF] hover:underline" data-testid={`admin-analytics-drill-${m.module}`}>Drill down →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Drill-down */}
      {selectedModule && detail && (
        <div className="mt-6 kr-card p-4" data-testid="admin-analytics-detail">
          <div className="flex items-center justify-between">
            <div>
              <div className="kr-overline">DRILL DOWN · {selectedModule.toUpperCase()}</div>
              <div className="text-xs text-white/50 mt-1">Last {days}d</div>
            </div>
            <button onClick={() => setSelectedModule(null)} className="text-white/50 text-xs hover:text-white">Close ×</button>
          </div>
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <div className="kr-overline mb-2">DAILY</div>
              <div className="h-48">
                <ResponsiveContainer>
                  <LineChart data={detail.daily}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" style={{ fontSize: 10 }} />
                    <YAxis stroke="rgba(255,255,255,0.4)" style={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "#050505", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }} />
                    <Line type="monotone" dataKey="events" stroke={MODULE_COLORS[selectedModule] || "#00E5FF"} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="errors" stroke="#EF4444" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div>
              <div className="kr-overline mb-2">TOP ACTIONS</div>
              <table className="w-full text-xs">
                <thead className="bg-[#050505] text-white/50"><tr><th className="text-left px-2 py-1.5 font-normal">Action</th><th className="text-right px-2 py-1.5 font-normal">#</th><th className="text-right px-2 py-1.5 font-normal">Credits</th><th className="text-right px-2 py-1.5 font-normal">Errors</th></tr></thead>
                <tbody>
                  {detail.actions.map((a) => (
                    <tr key={a.action} className="border-t border-white/5"><td className="px-2 py-1.5 font-mono">{a.action}</td><td className="px-2 py-1.5 text-right font-mono">{a.count}</td><td className="px-2 py-1.5 text-right font-mono">{a.credits}</td><td className="px-2 py-1.5 text-right font-mono">{a.errors}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {detail.top_users?.length > 0 && (
            <div className="mt-4">
              <div className="kr-overline mb-2">TOP USERS</div>
              <table className="w-full text-xs">
                <thead className="bg-[#050505] text-white/50"><tr><th className="text-left px-2 py-1.5 font-normal">User</th><th className="text-right px-2 py-1.5 font-normal">Events</th><th className="text-right px-2 py-1.5 font-normal">Credits</th></tr></thead>
                <tbody>
                  {detail.top_users.map((u) => (
                    <tr key={u.user_id} className="border-t border-white/5"><td className="px-2 py-1.5">{u.name || u.email || u.user_id}<span className="text-white/30 font-mono ml-2">{u.email}</span></td><td className="px-2 py-1.5 text-right font-mono">{u.events}</td><td className="px-2 py-1.5 text-right font-mono">{u.credits}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="kr-card p-4 relative overflow-hidden">
      <div className="kr-overline flex items-center gap-1.5"><Icon className="h-3 w-3" style={{ color: accent }} /> {label}</div>
      <div className="font-display text-3xl mt-2" style={{ color: accent }}>{Number(value || 0).toLocaleString()}</div>
      {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
    </div>
  );
}
